"use client";

import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X, CheckCircle, Info, AlertTriangle, AlertOctagon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ToastVariant } from "./toast.stories";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Viewport>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
    <ToastPrimitives.Viewport
        ref={ref}
        className={cn(
            "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
            className,
        )}
        {...props}
    />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
    "group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
    {
        variants: {
            variant: {
                default: "border bg-background text-foreground",
                destructive:
                    "destructive group border-destructive bg-destructive text-destructive-foreground",
                info: "border-blue-200 text-blue-900",
                warning: "border-yellow-200 text-yellow-900",
                error: "border-red-200 text-red-900",
                success: "border-green-200 text-green-900",
            },
            subvariant: {
                light: "",
                dark: "",
            },
        },
        compoundVariants: [
            {
                variant: "info",
                subvariant: "light",
                class: "bg-blue-50",
            },
            {
                variant: "info",
                subvariant: "dark",
                class: "bg-blue-900 border-blue-800 text-blue-50",
            },
            {
                variant: "warning",
                subvariant: "light",
                class: "bg-yellow-50",
            },
            {
                variant: "warning",
                subvariant: "dark",
                class: "bg-yellow-900 border-yellow-800 text-yellow-50",
            },
            {
                variant: "error",
                subvariant: "light",
                class: "bg-red-50",
            },
            {
                variant: "error",
                subvariant: "dark",
                class: "bg-red-900 border-red-800 text-red-50",
            },
            {
                variant: "success",
                subvariant: "light",
                class: "bg-green-50",
            },
            {
                variant: "success",
                subvariant: "dark",
                class: "bg-green-900 border-green-800 text-green-50",
            },
        ],
        defaultVariants: {
            variant: "default",
            subvariant: "light",
        },
    },
);

const Toast = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Root>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
        VariantProps<typeof toastVariants> & {
            icon?: React.ReactElement;
            variant?: ToastVariant;
        }
>(({ className, variant = "default", subvariant, icon, children, ...props }, ref) => {
    const defaultIcons: Record<string, React.ReactElement> = {
        default: <Info className="h-5 w-5 text-foreground" />,
        destructive: <AlertOctagon className="h-5 w-5 text-destructive" />,
        info: <Info className="h-5 w-5 text-blue-900" />,
        warning: <AlertTriangle className="h-5 w-5 text-yellow-900" />,
        error: <AlertOctagon className="h-5 w-5 text-red-900" />,
        success: <CheckCircle className="h-5 w-5 text-green-900" />,
    };

    return (
        <ToastPrimitives.Root
            ref={ref}
            className={cn(toastVariants({ variant, subvariant }), className)}
            {...props}
        >
            <ToastPrimitives.Title
                className={cn("text-sm font-semibold [&+div]:text-xs", className)}
            >
                <div className="flex gap-2">
                    {icon || defaultIcons[variant]}
                    {children}
                </div>
            </ToastPrimitives.Title>
        </ToastPrimitives.Root>
    );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Action>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
    <ToastPrimitives.Action
        ref={ref}
        className={cn(
            "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
            className,
        )}
        {...props}
    />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Close>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
    <ToastPrimitives.Close
        ref={ref}
        className={cn(
            "absolute right-1 top-1 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
            className,
        )}
        toast-close=""
        {...props}
    >
        <X className="h-4 w-4" />
    </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Title>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => {
    return (
        <ToastPrimitives.Title
            ref={ref}
            className={cn("text-sm font-semibold [&+div]:text-xs", className)}
            {...props}
        />
    );
});
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Description>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
    <ToastPrimitives.Description
        ref={ref}
        className={cn("text-sm opacity-90", className)}
        {...props}
    />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
    type ToastProps,
    type ToastActionElement,
    ToastProvider,
    ToastViewport,
    Toast,
    ToastTitle,
    ToastDescription,
    ToastClose,
    ToastAction,
};
